import React from 'react'
import { withFirebase } from '../../components/Fb'

// reactstrap components
import { Row, Col, Button, Card, CardBody } from 'reactstrap'
import FalconModal from '../../components/Inputs/FalconModal'
import {
  getEventTicketSchema,
  getEventTicketUISchema,
} from '../../components/Inputs/dataschemas'
import FalconLoading from '../../components/Inputs/FalconLoading'
import EditButton from '../../components/Buttons/EditButton'
import DeleteButton from '../../components/Buttons/DeleteButton'
import Infobox from '../../components/Infos/Infobox'
import moment from 'moment'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import { FormControl, InputLabel, Select, MenuItem, Alert, AlertTitle, Box } from '@mui/material'
import EventTicketParticipants from '../../components/Details/EventTicketParticipants'
import Plot from 'react-plotly.js'
import { activePurchases, mergeTicketsForReporting, soldCountByVariation } from '../../eventtickets'
import EventTicketGroupsDialog from '../../components/Dialogs/EventTicketGroupsDialog'
import {
  validateEventTicket,
  countProblems,
  isInShop,
  SEVERITY_ERROR,
} from '../../components/utils/eventticketvalidation'

// The validation code whose save is blocked in the edit modal, see isOversold.
const OVERSOLD = 'variation-oversold'

class EventTicketsContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      editModalOpen: false,
      eventtickets: null,
      deleteLoading: {},
      raw: [],
      images: [],
      memberships: [],
      groups: [],
      groupsDialogOpen: false,
      selectedGroupId: '',
      groupSalesOpen: false,
    }
  }

  componentDidMount() {
    this.updateData()
  }

  updateData = () => {
    this.props.firebase.getEventTickets().then((values) => {
      this.setState(
        {
          raw: values,
        },
        this.filterData
      )
    })
    this.props.firebase.getImages().then((images) => {
      this.setState({ images })
    })
    this.props.firebase.getMemberships().then((snap) => {
      const memberships = snap.empty ? [] : snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      this.setState({ memberships })
    })
    this.loadGroups()
  }

  handleRowEdit = (e) => {
    const _result = this.state.eventtickets.filter((r) => r.id === e)
    if (_result[0]) {
      // problems is derived in filterData and must be stripped like the other computed fields -
      // updateEventTicket writes whatever it is handed straight into the document.
      const { purchases, payments, pdfs, soldcount, soldamount, paid, ticketsavailable, actions, problems, ...formFields } = _result[0]
      // Documents last saved before the combination picker moved onto combinationId hold only the
      // chosen NAME in `combination`. The picker reads combinationId, so without this the row
      // would open blank and look unset. resolveCombinationIds does exactly this mapping on save
      // already, so reusing it keeps the two directions in step.
      if (formFields.variations) {
        // soldcount/soldamount are put on each variation by filterData, not stored fields.
        const definitions = formFields.variations.map(({ soldcount, soldamount, ...v }) => v)
        formFields.variations = this.props.firebase.resolveCombinationIds(definitions)
      }
      this.setState({ selectedResource: formFields }, this.toggleEditModal)
    }
  }

  toggleEventTicketsParticipantsDetails = () => {
    this.setState({
      detailsOpen: !this.state.detailsOpen,
    })
  }

  handleRowParticipants = (e) => {
    const _result = this.state.eventtickets.find((r) => r.id === e)
    this.setState({ selectedTicketEvent: _result, focusTab: 1 }, this.toggleEventTicketsParticipantsDetails)
  }

  toggleSalesInfo = () => {
    this.setState({
      salesInfoOpen: !this.state.salesInfoOpen,
    })
  }

  handleRowSalesInfo = (id) => {
    const _result = this.state.eventtickets.find((r) => r.id === id)
    this.setState({ selectedTicketEvent: _result }, this.toggleSalesInfo)
  }

  // The summary card and the breakdown graphs have to show the same numbers, so both read
  // this. soldcount is a ticket count; soldamount is EUROS - the summary prints it straight
  // through toFixed(2), unlike payments[].amount which is cents.
  // Section order follows the order the variations define them, matching the summary line.
  getSalesBreakdown = (ticket) => {
    const variations = ((ticket && ticket.variations) || []).map((v, idx) => ({
      key: v.id || String(idx),
      name: v.name || 'Lippu',
      count: v.soldcount || 0,
      euros: v.soldamount || 0,
      available: v.ticketsavailable || 0,
      section: v.section || '',
    }))
    const bySection = {}
    variations.forEach((v) => {
      if (!v.section) return
      if (!bySection[v.section]) bySection[v.section] = { name: v.section, count: 0, euros: 0, available: 0 }
      bySection[v.section].count += v.count
      bySection[v.section].euros += v.euros
      bySection[v.section].available += v.available
    })
    return { variations, sections: Object.keys(bySection).map((k) => bySection[k]) }
  }

  // One bar per label. euros formats the values to two decimals with a euro sign and
  // labels the axis; otherwise they are plain ticket counts. tall raises the height for
  // the per variation charts, which carry one bar per ticket type.
  renderBreakdownChart = (key, title, rows, euros, tall) => {
    const values = rows.map((r) => (euros ? r.euros : r.count))
    return (
      <div key={key} style={{ flex: '1 1 45%', minWidth: 320 }}>
        <Typography variant='subtitle1' style={{ marginBottom: 8 }}>
          {title}
        </Typography>
        <Plot
          data={[
            {
              x: rows.map((r) => r.name),
              y: values,
              type: 'bar',
              marker: { color: euros ? '#ef8157' : '#51cbce' },
              text: values.map((n) => (euros ? n.toFixed(2) + ' €' : String(n))),
              textposition: 'outside',
              textfont: { size: 11 },
              hovertemplate: euros ? '%{x}: %{y:.2f} €<extra></extra>' : '%{x}: %{y} kpl<extra></extra>',
            },
          ]}
          layout={{
            height: tall ? 448 : 320,
            autosize: true,
            margin: { l: 55, r: 20, t: 20, b: 40 },
            // type category is load bearing: sector names are often bare numbers ('4'),
            // and Plotly otherwise renders them on a continuous axis - 3.6, 3.8, 4, 4.2 -
            // instead of one labelled bar per sector.
            xaxis: { type: 'category', tickangle: -45, automargin: true },
            yaxis: { title: euros ? 'Euroa' : 'Lippua', rangemode: 'tozero' },
            showlegend: false,
          }}
          useResizeHandler
          style={{ width: '100%' }}
          config={{ displayModeBar: false }}
        />
      </div>
    )
  }

  // The three sales timelines. Lifted out of render so the group sales view can show the
  // same charts over a merged set of events instead of duplicating the Plotly config.
  renderTimelineCharts = (timeline) =>
    timeline ? (
                    <>
                    <div style={{ marginTop: 24 }}>
                      <Typography variant='h6' style={{ marginBottom: 8 }}>Myyntien aikajana (liput)</Typography>
                      <Plot
                        data={[
                          {
                            x: timeline.dates,
                            y: timeline.cumulativeCounts,
                            type: 'scatter',
                            mode: 'lines+markers',
                            name: 'Kumulatiivinen',
                            line: { color: '#ef8157' },
                            yaxis: 'y',
                          },
                          {
                            x: timeline.dates,
                            y: timeline.dailyCounts,
                            type: 'bar',
                            name: 'Päivittäin',
                            marker: { color: '#51cbce' },
                            yaxis: 'y2',
                            text: timeline.dailyCounts.map(String),
                            textposition: 'outside',
                            textfont: { size: 11 },
                          },
                        ]}
                        layout={{
                          height: 400,
                          autosize: true,
                          margin: { l: 50, r: 50, t: 30, b: 60 },
                          xaxis: { title: 'Päivämäärä', tickangle: -45 },
                          yaxis: { title: 'Yhteensä', side: 'left' },
                          yaxis2: { title: 'Päivittäin', side: 'right', overlaying: 'y', showgrid: false },
                          legend: { orientation: 'h', y: -0.25 },
                        }}
                        useResizeHandler
                        style={{ width: '100%' }}
                        config={{ displayModeBar: false }}
                      />
                    </div>
                    <div style={{ marginTop: 24 }}>
                      <Typography variant='h6' style={{ marginBottom: 8 }}>Myyntien aikajana (eurot)</Typography>
                      <Plot
                        data={[
                          {
                            x: timeline.dates,
                            y: timeline.cumulativeEuros,
                            type: 'scatter',
                            mode: 'lines+markers',
                            name: 'Kumulatiivinen',
                            line: { color: '#ef8157' },
                            yaxis: 'y',
                          },
                          {
                            x: timeline.dates,
                            y: timeline.dailyEuros,
                            type: 'bar',
                            name: 'Päivittäin',
                            marker: { color: '#51cbce' },
                            yaxis: 'y2',
                            text: timeline.dailyEuros.map((v) => v + ' €'),
                            textposition: 'outside',
                            textfont: { size: 11 },
                          },
                        ]}
                        layout={{
                          height: 400,
                          autosize: true,
                          margin: { l: 60, r: 60, t: 30, b: 60 },
                          xaxis: { title: 'Päivämäärä', tickangle: -45 },
                          yaxis: { title: 'Yhteensä (€)', side: 'left' },
                          yaxis2: { title: 'Päivittäin (€)', side: 'right', overlaying: 'y', showgrid: false },
                          legend: { orientation: 'h', y: -0.25 },
                        }}
                        useResizeHandler
                        style={{ width: '100%' }}
                        config={{ displayModeBar: false }}
                      />
                    </div>
                    {timeline.variationEuroSeries.length > 1 && (
                    <div style={{ marginTop: 24 }}>
                      <Typography variant='h6' style={{ marginBottom: 8 }}>Myynti lippulajeittain (€ / päivä)</Typography>
                      <Plot
                        data={[
                          ...timeline.variationEuroSeries.map((vs) => ({
                            x: timeline.dates,
                            y: vs.dailyEuros,
                            type: 'bar',
                            name: vs.name,
                            text: vs.dailyEuros.map((v) => v > 0 ? v + ' €' : ''),
                            textposition: 'outside',
                            textfont: { size: 11 },
                          })),
                          {
                            x: timeline.dates,
                            y: timeline.cumulativeEuros,
                            type: 'scatter',
                            mode: 'lines+markers',
                            name: 'Kumulatiivinen',
                            line: { color: '#ef8157' },
                            yaxis: 'y2',
                          },
                        ]}
                        layout={{
                          height: 400,
                          autosize: true,
                          barmode: 'stack',
                          margin: { l: 60, r: 60, t: 30, b: 60 },
                          xaxis: { title: 'Päivämäärä', tickangle: -45 },
                          yaxis: { title: 'Päivittäin (€)', side: 'left' },
                          yaxis2: { title: 'Yhteensä (€)', side: 'right', overlaying: 'y', showgrid: false },
                          legend: { orientation: 'h', y: -0.25 },
                        }}
                        useResizeHandler
                        style={{ width: '100%' }}
                        config={{ displayModeBar: false }}
                      />
                    </div>
                    )}
                    </>
                  ) : (
                    <Typography variant='body2' color='textSecondary' style={{ marginTop: 24 }}>
                      Ei myyntidataa aikajanalle.
                    </Typography>
                  )

  getSalesTimeline = (ticket) => {
    if (!ticket.purchases || !ticket.payments) return null
    // Cancelled purchases keep their payment, so they must be dropped before the join or
    // they would still show up in the sales chart.
    const paidPurchases = activePurchases(ticket).filter(
      (p) => p.paymenttoken && ticket.payments.some((pay) => pay.paymenttoken === p.paymenttoken)
    )
    if (paidPurchases.length === 0) return null
    const sorted = [...paidPurchases].sort((a, b) => {
      const aT = a.timestamp ? new Date(a.timestamp).getTime() : 0
      const bT = b.timestamp ? new Date(b.timestamp).getTime() : 0
      return aT - bT
    })
    const dailyMap = {}
    const dailyEuroMap = {}
    const variationDailyEuroMap = {}
    sorted.forEach((p) => {
      if (!p.timestamp) return
      const day = moment(p.timestamp).format('YYYY-MM-DD')
      dailyMap[day] = (dailyMap[day] || 0) + (p.quantity || 0)
      const euros = (p.price || 0) * (p.quantity || 0)
      dailyEuroMap[day] = (dailyEuroMap[day] || 0) + euros
      const vid = p.variationId || '_default'
      if (!variationDailyEuroMap[vid]) variationDailyEuroMap[vid] = {}
      variationDailyEuroMap[vid][day] = (variationDailyEuroMap[vid][day] || 0) + euros
    })
    const dates = Object.keys(dailyMap).sort()
    const dailyCounts = dates.map((d) => dailyMap[d])
    const dailyEuros = dates.map((d) => Math.round(dailyEuroMap[d]))
    const cumulativeCounts = []
    const cumulativeEuros = []
    let running = 0
    let runningEuros = 0
    dailyCounts.forEach((c, i) => {
      running += c
      cumulativeCounts.push(running)
      runningEuros += dailyEuros[i]
      cumulativeEuros.push(Math.round(runningEuros))
    })
    // Build per-variation daily euro series
    const variationNames = {}
    if (ticket.variations) {
      ticket.variations.forEach((v) => { variationNames[v.id] = v.name || 'Lippu' })
    }
    const variationEuroSeries = Object.keys(variationDailyEuroMap).map((vid) => ({
      id: vid,
      name: variationNames[vid] || 'Lippu',
      dailyEuros: dates.map((d) => Math.round(variationDailyEuroMap[vid][d] || 0)),
    }))
    const formattedDates = dates.map((d) => moment(d).format('DD.MM.YYYY'))
    return { dates: formattedDates, dailyCounts, cumulativeCounts, dailyEuros, cumulativeEuros, variationEuroSeries }
  }

  filterData = async () => {
    const data = []
    for (let i = 0; i < this.state.raw.length; i++) {
      const item = { ...this.state.raw[i] }
      // Only count purchases that have a paymenttoken found in payments
      // Cancelled purchases must not count towards sold or revenue, otherwise the seat can
      // never be resold.
      const paidPurchases = item.purchases && item.payments
        ? activePurchases(item).filter((p) => p.paymenttoken && item.payments.some((pay) => pay.paymenttoken === p.paymenttoken))
        : []
      const sold = paidPurchases.reduce((acc, curr) => {
        acc.quantity += curr.quantity || 0
        acc.amount += curr.price * curr.quantity || 0
        return acc
      }, { quantity: 0, amount: 0 })
      const maxavailability = item.variations ? item.variations.reduce((acc, curr) => {
        acc.ticketsavailable += curr.ticketsavailable || 0
        return acc
      }, { ticketsavailable: 0 }) : { ticketsavailable: 0 }
      const paid = item.payments ? item.payments.reduce((acc, curr) => {
        acc += curr.amount || 0
        return acc
      }, 0) : 0
      // The ticket COUNT follows the backend's rule (soldPurchases): an unpaid line still holds
      // its ticket until its validuntil passes, and an admin sale without payment holds it for
      // good. Revenue above stays on paid lines only.
      const soldByVariation = soldCountByVariation(item)
      item.soldcount = Object.keys(soldByVariation).reduce((sum, vid) => sum + soldByVariation[vid], 0)
      item.ticketsavailable = maxavailability.ticketsavailable || 0
      item.soldamount = sold.amount / 100 || 0
      item.paid = paid / 100 || 0
      // Enrich each variation with its sold count (only paid purchases)
      if (item.variations) {
        item.variations = item.variations.map((v) => {
          const variationSold = soldByVariation[v.id] || 0
          const variationPaid = paidPurchases
            .filter((p) => p.variationId === v.id)
            .reduce((acc, p) => acc + (p.quantity || 0), 0)
          const variationSoldAmount = variationPaid * (v.price || 0)
          return { ...v, soldcount: variationSold, soldamount: variationSoldAmount }
        })
      }
      // Validate the DEFINITION, not the sales. Done once here rather than in render so the
      // banner and the card chip cannot disagree, and so filtering by group does not re-walk
      // every variation of every ticket.
      item.problems = validateEventTicket(item)
      data.push(item)
    }
    // Sort by start date ascending
    data.sort((a, b) => {
      const aTime = a.start ? new Date(a.start).getTime() : 0
      const bTime = b.start ? new Date(b.start).getTime() : 0
      return bTime - aTime
    })
    this.setState({ loading: false, eventtickets: data })
  }

  getPoster = (imageObj) => {
    if (!imageObj || !imageObj.url) return null
    const { url } = imageObj
    const parts = url.split('/')
    const filename = parts.pop()
    return [...parts, '600_' + filename].join('/')
  }

  deleteCard = async (id) => {
    await this.props.firebase.deleteBenefitCard(id)
    this.setState({ loading: true }, this.updateData)
  }

  addNewCard = () => {
    this.setState({ selectedResource: {} }, this.toggleEditModal)
  }

  toggleEditModal = () => {
    this.setState({
      editModalOpen: !this.state.editModalOpen,
    })
  }

  loadGroups = async () => {
    const groups = await this.props.firebase.getEventTicketGroups()
    // Drop a selection that no longer exists. Deleting the selected group otherwise hides the
    // filter row - it only renders when there are groups - while the filter stays applied,
    // leaving the list stuck showing a subset with no control left to clear it.
    const stillExists = groups.some((g) => g.id === this.state.selectedGroupId)
    this.setState({
      groups,
      ...(stillExists ? {} : { selectedGroupId: '', groupSalesOpen: false }),
    })
  }

  toggleGroupsDialog = () => {
    this.setState({ groupsDialogOpen: !this.state.groupsDialogOpen })
  }

  handleGroupFilterChange = (event) => {
    this.setState({ selectedGroupId: event.target.value })
  }

  toggleGroupSales = () => {
    this.setState({ groupSalesOpen: !this.state.groupSalesOpen })
  }

  getGroupName = (id) => {
    if (!id) return ''
    const group = (this.state.groups || []).find((g) => g.id === id)
    // A soft deleted group keeps its id on the ticket; say so rather than showing nothing.
    return group ? group.name : 'Poistettu ryhmä'
  }

  // How many event tickets point at each group, for the manage dialog's delete confirmation.
  getGroupUsage = () => {
    return (this.state.eventtickets || []).reduce((acc, t) => {
      if (!t.eventticketgroup) return acc
      acc[t.eventticketgroup] = (acc[t.eventticketgroup] || 0) + 1
      return acc
    }, {})
  }

  getGroupTickets = (groupid) =>
    (this.state.eventtickets || []).filter((t) => t.eventticketgroup === groupid)

  // The tickets the grid is currently showing, so the banner never names an event the admin
  // cannot see because a group filter is on.
  visibleTickets = () =>
    this.state.selectedGroupId
      ? this.getGroupTickets(this.state.selectedGroupId)
      : this.state.eventtickets || []

  // Errors only. A warning is worth a chip on the card, but putting it up here would bury the one
  // thing that matters: these events take the customer's money and then fail to deliver a ticket.
  // See utils/eventticketvalidation.js for what happened on 2026-09-11.
  renderTicketProblems = () => {
    const broken = this.visibleTickets()
      .map((ticket) => ({
        ticket,
        errors: (ticket.problems || []).filter((p) => p.severity === SEVERITY_ERROR),
      }))
      .filter(({ errors }) => errors.length > 0)
    if (broken.length === 0) return null
    return (
      <Alert severity='error' sx={{ mb: 2 }}>
        <AlertTitle>Tapahtumia, joiden osto epäonnistuu ({broken.length})</AlertTitle>
        <Typography variant='body2' sx={{ mb: 1 }}>
          Näissä tapahtumissa on virhe, joka kaataa lipun toimituksen vasta maksun jälkeen:
          asiakkaalta veloitetaan, mutta PDF-lippua eikä vahvistusviestiä lähetetä. Korjaa
          tapahtuma tai poista se verkkokaupasta.
        </Typography>
        {broken.map(({ ticket, errors }) => (
          <Typography key={ticket.id} variant='body2'>
            <b>{ticket.name || '(nimetön tapahtuma)'}</b> — {errors.map((e) => e.message).join(' ')}
          </Typography>
        ))}
      </Alert>
    )
  }

  // The form holds the definition only, so the oversold check needs the sold counts that
  // filterData computed for the stored ticket, matched by variation id.
  withSoldCounts = (formData) => {
    const stored = formData.id && (this.state.eventtickets || []).find((t) => t.id === formData.id)
    if (!stored || !Array.isArray(formData.variations)) return formData
    const sold = {}
    ;(stored.variations || []).forEach((v) => { if (v && v.id) sold[v.id] = v.soldcount || 0 })
    return {
      ...formData,
      variations: formData.variations.map((v) => (v && v.id && sold[v.id] !== undefined ? { ...v, soldcount: sold[v.id] } : v)),
    }
  }

  // The same problems, live, under the Tallenna button while the admin types. draft: true because
  // variation ids and combinationId are both filled in by the save path (firebase.js), so a row
  // added a second ago legitimately has neither yet.
  //
  // Saving is deliberately NOT blocked: a ticket is built up over several sittings and is
  // incomplete for most of that time. The banner and the card chip are what carry the warning
  // once it is stored.
  //
  // The one exception is an oversold variation: tickets available below what is already sold
  // makes the backend refuse every further sale of it, so that save is blocked (see
  // isOversold). The counts are from when the list loaded, which is close enough to stop a typo.
  modalProblems = (formData) => validateEventTicket(this.withSoldCounts(formData || {}), { draft: true })

  isOversold = (formData) => this.modalProblems(formData).some((p) => p.code === OVERSOLD)

  renderModalProblems = (formData) => {
    const all = this.modalProblems(formData)
    if (all.length === 0) return null
    const oversold = all.filter((p) => p.code === OVERSOLD)
    const problems = all.filter((p) => p.code !== OVERSOLD)
    const { errors } = countProblems(problems)
    return (
      <Box sx={{ width: '100%' }}>
        {oversold.length > 0 && (
          <Alert severity='error' sx={{ mb: problems.length ? 1 : 0 }}>
            <AlertTitle>Tallennus estetty</AlertTitle>
            {oversold.map((p, i) => (
              <Typography key={p.code + i} variant='body2'>
                {p.message}
              </Typography>
            ))}
            <Typography variant='body2'>Saatavilla olevien lippujen määrä ei voi olla pienempi kuin jo myytyjen.</Typography>
          </Alert>
        )}
        {problems.length > 0 && (
        <Alert severity={errors ? 'error' : 'warning'}>
          <AlertTitle>
            {errors ? 'Korjattavaa (' + errors + ')' : 'Puutteita (' + problems.length + ')'}
          </AlertTitle>
          {problems.map((p, i) => (
            <Typography key={p.code + i} variant='body2'>
              {p.message}
            </Typography>
          ))}
        </Alert>
        )}
      </Box>
    )
  }

  getEnhancedCardSchema = () => {
    const schema = getEventTicketSchema()
    // const sport = { ...schema.properties.sport }
    // if (this.state.sports) {
    //   for (let i = 0; i < this.state.sports.length; i++) {
    //     sport.enum.push(this.state.sports[i].label)
    //     sport.enumNames.push(this.state.sports[i].label)
    //   }
    // }
    // schema.properties.sport = sport
    if (this.state.images) {
      schema.dependencies.availableinshop.oneOf[0].properties.image.items.images =
        this.state.images
      schema.properties.ticketimage.items.images = this.state.images
    }
    // The empty option MUST stay first: rjsf adopts the first oneOf const as a default for a
    // field with no stored value, so any other order would stamp untouched tickets with a
    // group on their next save. See the note in schemas/eventticket.js.
    schema.properties.eventticketgroup.oneOf = [
      { const: '', title: 'Ei ryhmää' },
      ...(this.state.groups || []).map((g) => ({ const: g.id, title: g.name })),
    ]
    if (this.state.memberships && this.state.memberships.length > 0) {
      const membershipOneOf = this.state.memberships.map((mem) => ({
        const: mem.id,
        title: (mem.displayName || mem.name) + (mem.state === 'deleted' ? ' (poistettu)' : ''),
      }))
      schema.properties.variations.items.properties.membershipPrices.items.properties.membership.oneOf =
        membershipOneOf
    }
    return schema
  }

  createCardModal = () => {
    return (
      <FalconModal
        onUpdate={this.props.firebase.updateEventTicket}
        onCreate={this.props.firebase.createEventTicket}
        modifiedCallback={this.updateData}
        toggle={this.toggleEditModal}
        resource={this.state.selectedResource}
        isOpen={this.state.editModalOpen}
        uiSchema={getEventTicketUISchema()}
        dataSchema={this.getEnhancedCardSchema()}
        renderFooter={this.renderModalProblems}
        disableSave={this.isOversold}
        title={
          this.state.selectedResource && this.state.selectedResource.id
            ? 'Muokkaa tapahtumaa'
            : 'Uusi tapahtuma'
        }
        size='lg'
      />
    )
  }

  render() {
    if (this.state.loading) {
      return <FalconLoading />
    } else if (this.state.detailsOpen) {
      return (
        <>
          <div className='content'>
            <Row>
              <Col>
                <Button onClick={this.toggleEventTicketsParticipantsDetails}>Palaa listaan</Button>
              </Col>
            </Row>
            <EventTicketParticipants eventticket={this.state.selectedTicketEvent} toggle={this.toggleEventTicketsParticipantsDetails} />
          </div>
        </>
      )
    } else if (this.state.salesInfoOpen) {
      const ticket = this.state.selectedTicketEvent
      const membershipNameById = (this.state.memberships || []).reduce((acc, m) => {
        acc[m.id] = m.displayName || m.name
        return acc
      }, {})
      const timeline = this.getSalesTimeline(ticket)
      const posterUrl = this.getPoster(ticket.ticketimage || ticket.image)
      return (
        <>
          <div className='content'>
            <Row>
              <Col>
                <Button onClick={this.toggleSalesInfo}>Palaa listaan</Button>
              </Col>
            </Row>
            <Card style={{ marginTop: 16 }}>
              <CardBody>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  {posterUrl && (
                    <img
                      src={posterUrl}
                      alt={ticket.name}
                      style={{ width: 300, height: 300, objectFit: 'cover', borderRadius: 4 }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 280 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <Typography variant='h5' style={{ fontWeight: 600 }}>
                        {ticket.name}
                      </Typography>
                      {isInShop(ticket) && (
                        <Chip label='Verkkokaupassa' size='small' color='primary' />
                      )}
                    </div>
                    <Typography variant='subtitle1' color='textSecondary' style={{ marginBottom: 16 }}>
                      {ticket.start ? moment.utc(ticket.start).format('DD.MM.YYYY HH:mm') : ''}
                    </Typography>

                    {ticket.variations && ticket.variations.length > 0 && (
                      <div>
                        {ticket.variations.map((v, idx) => (
                          <div
                            key={v.id || idx}
                            style={{
                              padding: '8px 0',
                              borderBottom: idx < ticket.variations.length - 1 ? '1px solid #eee' : 'none',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Typography variant='body1'>{v.name || 'Lippu'}</Typography>
                              <div style={{ display: 'flex', gap: 24 }}>
                                <Typography variant='body1' color='textSecondary'>
                                  {v.soldcount || 0} / {v.ticketsavailable || 0}
                                </Typography>
                                <Typography variant='body1'>
                                  {(v.soldamount || 0).toFixed(2)} €
                                </Typography>
                              </div>
                            </div>
                            {v.membershipPrices && v.membershipPrices.length > 0 && (
                              <Typography variant='body2' color='textSecondary' style={{ paddingLeft: 8, marginTop: 4 }}>
                                Jäsenhinnat: {v.membershipPrices
                                  .map((mp) => `${membershipNameById[mp.membership] || mp.membership || 'Jäsen'} ${mp.price} €`)
                                  .join(' · ')}
                              </Typography>
                            )}
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, marginTop: 8, borderTop: '2px solid #ddd', paddingTop: 8 }}>
                          <Typography variant='body1' style={{ fontWeight: 700 }}>
                            Yht: {ticket.variations.reduce((acc, v) => acc + (v.soldcount || 0), 0)} / {ticket.ticketsavailable || 0}
                          </Typography>
                          <Typography variant='body1' style={{ fontWeight: 700 }}>
                            {ticket.variations.reduce((acc, v) => acc + (v.soldamount || 0), 0).toFixed(2)} €
                          </Typography>
                          <Typography variant='body1' style={{ fontWeight: 700 }}>
                            (maksettu: {(ticket.paid || 0).toFixed(2)} €)
                          </Typography>
                        </div>
                        {(() => {
                          // Same helper the breakdown graphs below use, so the two cannot drift.
                          const sections = this.getSalesBreakdown(ticket).sections
                          if (sections.length === 0) return null
                          return (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginTop: 8, borderTop: '1px solid #eee', paddingTop: 8 }}>
                              {sections.map((section) => (
                                <Typography key={section.name} variant='body1' color='textSecondary'>
                                  Sektori {section.name}: {section.count} / {section.available}
                                </Typography>
                              ))}
                            </div>
                          )
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* The summary numbers above, as graphs: per ticket variation and, when the
                    variations define sections, per sector. */}
                {(() => {
                  const breakdown = this.getSalesBreakdown(ticket)
                  if (breakdown.variations.length === 0) return null
                  const hasSections = breakdown.sections.length > 0
                  return (
                    <div style={{ marginTop: 24 }}>
                      <Typography variant='h6' style={{ marginBottom: 8 }}>
                        Myynti lippulajeittain ja sektoreittain
                      </Typography>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                        {this.renderBreakdownChart('varcount', 'Myynti lippulajeittain (määrä)', breakdown.variations, false, true)}
                        {this.renderBreakdownChart('vareuro', 'Myynti lippulajeittain (Eurot)', breakdown.variations, true, true)}
                        {hasSections && this.renderBreakdownChart('seccount', 'Myynti sektoreittain (määrä)', breakdown.sections, false, false)}
                        {hasSections && this.renderBreakdownChart('seceuro', 'Myynti sektoreittain (Eurot)', breakdown.sections, true, false)}
                      </div>
                    </div>
                  )
                })()}

                {this.renderTimelineCharts(timeline)}
              </CardBody>
            </Card>
          </div>
        </>
      )
    } else if (this.state.groupSalesOpen) {
      const groupid = this.state.selectedGroupId
      const tickets = this.getGroupTickets(groupid)
      // Merged into one synthetic ticket so the SAME breakdown and timeline functions a
      // single event uses can render a whole group. See mergeTicketsForReporting.
      const merged = mergeTicketsForReporting(tickets)
      const breakdown = this.getSalesBreakdown(merged)
      const timeline = this.getSalesTimeline(merged)
      const hasSections = breakdown.sections.length > 0
      return (
        <>
          <div className='content'>
            <Row>
              <Col>
                <Button onClick={this.toggleGroupSales}>Palaa listaan</Button>
              </Col>
            </Row>
            <Card style={{ marginTop: 16 }}>
              <CardBody>
                <Typography variant='h5' style={{ fontWeight: 600 }}>
                  {this.getGroupName(groupid)}
                </Typography>
                <Typography variant='subtitle1' color='textSecondary' style={{ marginBottom: 16 }}>
                  {merged.eventcount === 1 ? '1 tapahtuma' : merged.eventcount + ' tapahtumaa'}
                </Typography>

                {breakdown.variations.length === 0 ? (
                  <Typography variant='body2' color='textSecondary'>
                    Ryhmään ei kuulu tapahtumia, joissa olisi lippulajeja.
                  </Typography>
                ) : (
                  <>
                    <div>
                      {breakdown.variations.map((v) => (
                        <div
                          key={v.key}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 0',
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          <Typography variant='body1'>{v.name}</Typography>
                          <div style={{ display: 'flex', gap: 24 }}>
                            <Typography variant='body1' color='textSecondary'>
                              {v.count} / {v.available}
                            </Typography>
                            <Typography variant='body1'>{v.euros.toFixed(2)} €</Typography>
                          </div>
                        </div>
                      ))}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, marginTop: 8, borderTop: '2px solid #ddd', paddingTop: 8 }}>
                        <Typography variant='body1' style={{ fontWeight: 700 }}>
                          Yht: {merged.soldcount} / {merged.ticketsavailable}
                        </Typography>
                        <Typography variant='body1' style={{ fontWeight: 700 }}>
                          {merged.soldamount.toFixed(2)} €
                        </Typography>
                        <Typography variant='body1' style={{ fontWeight: 700 }}>
                          (maksettu: {merged.paid.toFixed(2)} €)
                        </Typography>
                      </div>
                      {hasSections && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginTop: 8, borderTop: '1px solid #eee', paddingTop: 8 }}>
                          {breakdown.sections.map((section) => (
                            <Typography key={section.name} variant='body1' color='textSecondary'>
                              Sektori {section.name}: {section.count} / {section.available}
                            </Typography>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ marginTop: 24 }}>
                      <Typography variant='h6' style={{ marginBottom: 8 }}>
                        Myynti lippulajeittain ja sektoreittain
                      </Typography>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                        {this.renderBreakdownChart('gvarcount', 'Myynti lippulajeittain (määrä)', breakdown.variations, false, true)}
                        {this.renderBreakdownChart('gvareuro', 'Myynti lippulajeittain (Eurot)', breakdown.variations, true, true)}
                        {hasSections && this.renderBreakdownChart('gseccount', 'Myynti sektoreittain (määrä)', breakdown.sections, false, false)}
                        {hasSections && this.renderBreakdownChart('gseceuro', 'Myynti sektoreittain (Eurot)', breakdown.sections, true, false)}
                      </div>
                    </div>

                    {this.renderTimelineCharts(timeline)}
                  </>
                )}
              </CardBody>
            </Card>
          </div>
        </>
      )
    } else {
      return (
        <>
          <div className='content'>
            <Row>
              <Col>
                <Button onClick={this.addNewCard}>
                  Lisää tapahtuma johon myydään lippuja
                </Button>{' '}
                <Button onClick={this.toggleGroupsDialog}>Hallitse ryhmiä</Button>
              </Col>
              <Col>
                <Infobox index={'0'} />
                {/* <Alert severity='info'>
                  <strong>Info!</strong> Sarjakortit ovat aktiviteetteihin/tapahtumiin liittyviä kortteja.
                </Alert> */}
              </Col>
            </Row>
            {this.state.groups && this.state.groups.length > 0 && (
              <Row className='mt-2'>
                <Col xs={12} md={4}>
                  <FormControl fullWidth size='small'>
                    <InputLabel id='eventticketgroup-filter-label'>Tapahtumaryhmä</InputLabel>
                    <Select
                      labelId='eventticketgroup-filter-label'
                      label='Tapahtumaryhmä'
                      value={this.state.selectedGroupId}
                      onChange={this.handleGroupFilterChange}
                    >
                      <MenuItem value=''>Kaikki tapahtumat</MenuItem>
                      {this.state.groups.map((g) => (
                        <MenuItem key={g.id} value={g.id}>
                          {g.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>
                {this.state.selectedGroupId && (
                  <Col xs={12} md={4} className='mt-2'>
                    <Button color='primary' onClick={this.toggleGroupSales}>
                      Ryhmän myyntitiedot
                    </Button>
                  </Col>
                )}
              </Row>
            )}
            <div style={{ marginTop: 16 }}>{this.renderTicketProblems()}</div>
            <Grid container spacing={2} style={{ marginTop: 8 }}>
              {this.visibleTickets().map((ticket) => (
                <Grid size={{ xs: 12, md: 12, lg: 12, xl: 6 }} key={ticket.id}>
                  <Card style={{ height: '100%', marginBottom: 0 }}>
                    <div style={{ display: 'flex', height: '100%' }}>
                      {this.getPoster(ticket.ticketimage || ticket.image) && (
                        <div
                          style={{
                            width: 340,
                            minHeight: 340,
                            flexShrink: 0,
                            aspectRatio: '1 / 1',
                            backgroundImage: `url(${this.getPoster(ticket.ticketimage || ticket.image)})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            borderRadius: '4px 0 0 4px',
                          }}
                        />
                      )}
                      <CardBody style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                          <div>
                            <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
                              {ticket.name}
                            </Typography>
                            <Typography variant='body2' color='textSecondary'>
                              {ticket.start ? moment.utc(ticket.start).format('DD.MM.YYYY HH:mm') : ''}
                            </Typography>
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {ticket.eventticketgroup && (
                              <Chip label={this.getGroupName(ticket.eventticketgroup)} size='small' variant='outlined' />
                            )}
                            {isInShop(ticket) && (
                              <Chip label='Verkkokaupassa' size='small' color='primary' />
                            )}
                            {/* Clicking opens the editor, so the warning leads straight to the fix. */}
                            {(() => {
                              const { errors, warnings } = countProblems(ticket.problems)
                              if (!errors && !warnings) return null
                              return (
                                <Chip
                                  label={errors ? 'Korjaa (' + errors + ')' : 'Puutteita (' + warnings + ')'}
                                  size='small'
                                  color={errors ? 'error' : 'warning'}
                                  variant={errors ? 'filled' : 'outlined'}
                                  onClick={() => this.handleRowEdit(ticket.id)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              )
                            })()}
                          </div>
                        </div>

                        <div style={{ flex: 1 }}>
                          {ticket.variations && ticket.variations.length > 0 ? (
                            ticket.variations.map((v, idx) => (
                              <div
                                key={v.id || idx}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '4px 0',
                                  borderBottom: idx < ticket.variations.length - 1 ? '1px solid #eee' : 'none',
                                }}
                              >
                                <Typography variant='body2'>{v.name || 'Lippu'}</Typography>
                                <div style={{ display: 'flex', gap: 12 }}>
                                  <Typography variant='body2' color='textSecondary'>
                                    {v.soldcount || 0} / {v.ticketsavailable || 0}
                                  </Typography>
                                  <Typography variant='body2'>
                                    {(v.soldamount || 0).toFixed(2)} €
                                  </Typography>
                                </div>
                              </div>
                            ))
                          ) : (
                            <Typography variant='body2' color='textSecondary'>
                              {ticket.soldcount || 0} / {ticket.ticketsavailable || 0} myyty
                            </Typography>
                          )}
                        </div>

                        {ticket.variations && ticket.variations.length > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 4, borderTop: '1px solid #ddd', paddingTop: 4 }}>
                            <Typography variant='body2' style={{ fontWeight: 600 }}>
                              Yht: {ticket.variations.reduce((acc, v) => acc + (v.soldcount || 0), 0)} / {ticket.ticketsavailable || 0}
                            </Typography>
                            <Typography variant='body2' style={{ fontWeight: 600 }}>
                              {ticket.variations.reduce((acc, v) => acc + (v.soldamount || 0), 0).toFixed(2)} €
                            </Typography>
                            <Typography variant='body2' style={{ fontWeight: 600 }}>
                              (maksettu: {(ticket.paid || 0).toFixed(2)} €)
                            </Typography>
                          </div>
                        )}
                        {ticket.variations && ticket.variations.length > 0 && (() => {
                          const sectionTotals = ticket.variations.reduce((acc, v) => {
                            const section = v.section || ''
                            if (!section) return acc
                            if (!acc[section]) acc[section] = { sold: 0, available: 0 }
                            acc[section].sold += v.soldcount || 0
                            acc[section].available += v.ticketsavailable || 0
                            return acc
                          }, {})
                          const sections = Object.keys(sectionTotals)
                          if (sections.length === 0) return null
                          return (
                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 12, marginTop: 4 }}>
                              {sections.map((section) => (
                                <Typography key={section} variant='body2' color='textSecondary'>
                                  Sektori {section}: {sectionTotals[section].sold} / {sectionTotals[section].available}
                                </Typography>
                              ))}
                            </div>
                          )
                        })()}

                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                          <EditButton onClick={() => this.handleRowEdit(ticket.id)} />
                          <Button size='sm' color='info' onClick={() => this.handleRowParticipants(ticket.id)}>
                            <i className='nc-icon nc-badge' /> Lippulista
                          </Button>
                          <Button size='sm' color='default' onClick={() => this.handleRowSalesInfo(ticket.id)}>
                            <i className='nc-icon nc-chart-bar-32' /> Myyntitiedot
                          </Button>
                          <DeleteButton
                            loading={this.state.deleteLoading[ticket.id]}
                            onClick={() => this.deleteCard(ticket.id)}
                          />
                        </div>
                      </CardBody>
                    </div>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {this.state.alert}
            {this.createCardModal()}
            <EventTicketGroupsDialog
              open={this.state.groupsDialogOpen}
              onClose={this.toggleGroupsDialog}
              onChanged={this.loadGroups}
              groups={this.state.groups}
              usageCount={this.getGroupUsage()}
            />
          </div>
        </>
      )
    }
  }
}

export default withFirebase(EventTicketsContainer)
